import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  LinearProgress,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';

const Subscriptions = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/auth/me');
      if (response.success && response.data?.tenant?.subscription) {
        setSubscription(response.data.tenant.subscription);
      } else {
        setError('No subscription information found');
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
      setError('Failed to fetch subscription details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch subscription history
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  
  const fetchSubscriptionHistory = async () => {
    try {
      const response = await apiService.get('/subscriptions/history');
      if (response.success) {
        setSubscriptionHistory(response.data.subscriptions || []);
      }
    } catch (error) {
      console.error('Error fetching subscription history:', error);
    }
  };
  
  useEffect(() => {
    fetchSubscriptionHistory();
  }, []);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'suspended':
        return 'warning';
      case 'cancelled':
        return 'default';
      default:
        return 'info';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return <CheckCircleIcon />;
      case 'expired':
        return <ErrorIcon />;
      case 'suspended':
        return <WarningIcon />;
      case 'cancelled':
        return <InfoIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };




  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading subscription details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Subscription Details
        </Typography>
      </Box>

      {/* Current Plan Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {subscription?.plan_name || 'FREE Trial'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {subscription?.plan_description || '30-day free trial with full access to all features'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <Chip
                icon={getStatusIcon(subscription?.status)}
                label={subscription?.status || 'Active'}
                color={getStatusColor(subscription?.status)}
                size="large"
                sx={{ fontWeight: 'bold' }}
              />

            </Box>
          </Box>

          {/* Subscription Management Section */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              Subscription Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subscription?.status === 'active' 
                ? `Your ${subscription?.plan_name || 'plan'} is active. View subscription history below.`
                : 'Manage your subscription plan'
              }
            </Typography>
          </Box>



          {/* Plan Features */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Plan Features
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="User Management" secondary="Manage staff and users" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Appointment Booking" secondary="Online booking system" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Client Management" secondary="Client database and history" />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Invoice Management" secondary="Create and manage invoices" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Email Templates" secondary="Customizable email communications" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Data Export" secondary="Export data in various formats" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Subscription History */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon color="primary" />
            Subscription History
          </Typography>
          
          {subscriptionHistory.length > 0 ? (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Subscription #</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Plan</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                    {/* <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Base Amount</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>GST (18%)</th> */}
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}> Amount</th>
                    {/* <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Payment Method</th> */}
                                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Start Date</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>End Date</th>
                      {/* <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Next Billing Date</th> */}
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Actions</th>
                    </tr>
                  </thead>
                <tbody>
                  {subscriptionHistory.map((sub, index) => (
                    <tr key={sub.id || index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {sub.subscription_number}
                        </Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {sub.plan_name || 'N/A'}
                        </Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Chip
                          icon={getStatusIcon(sub.status)}
                          label={sub.status || 'Unknown'}
                          color={getStatusColor(sub.status)}
                          size="small"
                        />
                      </td>
                      {/* <td style={{ padding: '12px' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {sub.payment_amount ? `₹${sub.payment_amount}` : 'N/A'}
                        </Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2" color="text.secondary">
                          {sub.tax_amount ? `₹${sub.tax_amount}` : 'N/A'}
                        </Typography>
                      </td> */}
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {sub.total_amount ? `₹${sub.total_amount}` : 'N/A'}
                        </Typography>
                      </td>
                      {/* <td style={{ padding: '12px' }}>
                        <Typography variant="body2" color="text.secondary">
                          {sub.payment_method || 'N/A'}
                        </Typography>
                      </td> */}
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2">
                          {formatDate(sub.start_date)}
                        </Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2">
                          {formatDate(sub.end_date || sub.expires_at)}
                        </Typography>
                      </td>
                      {/* <td style={{ padding: '12px' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(sub.next_billing_date)}
                        </Typography>
                      </td> */}
                      <td style={{ padding: '12px' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ReceiptIcon />}
                          onClick={() => navigate(`/subscriptions/invoices/${sub.id}`)}
                          sx={{ 
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            minWidth: 'auto'
                          }}
                        >
                          View Receipt
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No subscription history found
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Important Notes */}
      <Card sx={{ mt: 3, bgcolor: 'info.light' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="info" />
            Important Information
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • View your complete subscription history in the table above
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Active subscriptions are highlighted in green
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Contact support for any subscription-related questions
          </Typography>
          <Typography variant="body2">
            • Contact support to change your plan
          </Typography>
        </CardContent>
      </Card>




    </Box>
  );
};

export default Subscriptions;
